export interface EventBlock {
  type: 'heading' | 'paragraph' | 'quote' | 'list';
  text?: string;
  items?: string[];
}

export interface EventTimelineItem {
  time: string;
  text: string;
}

export interface EventItem {
  id: string;
  title: string;
  categoryId: string;
  category: string;
  campus: string;
  summary: string;
  cover: string;
  heat: number;
  comments: number;
  publishedAt: string;
  readTime: string;
  tags: string[];
  featured?: boolean;
  editorPick?: boolean;
  timeline: EventTimelineItem[];
  content: EventBlock[];
}

export interface CategoryItem {
  id: string;
  name: string;
  icon: string;
  desc: string;
  chip: string;
  dot: string;
  cover: string;
}

export const categories: CategoryItem[] = [
  {
    id: 'campus-life',
    name: '校园生活',
    icon: 'ri-community-line',
    desc: '军训、活动、校园日常里那些让人会心一笑的现场',
    chip: 'bg-primary-100 text-primary-700',
    dot: 'bg-primary-500',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20campus%20lawn%20with%20students%20walking%20and%20chatting%2C%20soft%20afternoon%20sunlight%2C%20coral%20and%20amber%20color%20grading%2C%20editorial%20photography%2C%20cozy%20cheerful%20atmosphere%2C%20soft%20cream%20background%2C%20no%20text&width=800&height=600&seq=cat-campus-life-01&orientation=landscape',
  },
  {
    id: 'study-exam',
    name: '学业考场',
    icon: 'ri-book-open-line',
    desc: '选课、考试周、自习室，学业里的大型围观现场',
    chip: 'bg-accent-100 text-accent-900',
    dot: 'bg-accent-500',
    cover:
      'https://readdy.ai/api/search-image?query=university%20study%20room%20at%20night%20with%20stacks%20of%20books%20and%20warm%20desk%20lamps%2C%20students%20studying%20quietly%2C%20amber%20warm%20tone%20grading%2C%20cozy%20academic%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=cat-study-exam-01&orientation=landscape',
  },
  {
    id: 'dorm-daily',
    name: '宿舍日常',
    icon: 'ri-home-smile-line',
    desc: '宿舍楼里的猫、阳台上的花，还有隔壁的烟火气',
    chip: 'bg-secondary-100 text-secondary-900',
    dot: 'bg-secondary-500',
    cover:
      'https://readdy.ai/api/search-image?query=cozy%20university%20dormitory%20balcony%20with%20potted%20plants%20and%20warm%20string%20lights%20at%20dusk%2C%20soft%20amber%20and%20coral%20tones%2C%20gentle%20homely%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=cat-dorm-daily-01&orientation=landscape',
  },
  {
    id: 'club-activity',
    name: '社团活动',
    icon: 'ri-group-line',
    desc: '招新、汇演、比赛，社团人把操场变成了舞台',
    chip: 'bg-primary-100 text-primary-700',
    dot: 'bg-primary-500',
    cover:
      'https://readdy.ai/api/search-image?query=university%20club%20fair%20on%20a%20sports%20field%20with%20colorful%20booths%20and%20string%20lights%20at%20dusk%2C%20energetic%20crowd%2C%20warm%20coral%20and%20golden%20tones%2C%20festival%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=cat-club-activity-01&orientation=landscape',
  },
  {
    id: 'campus-food',
    name: '校园美食',
    icon: 'ri-restaurant-2-line',
    desc: '食堂新窗口、深夜小吃摊，排队就是最好的推荐',
    chip: 'bg-accent-100 text-accent-900',
    dot: 'bg-accent-500',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20inviting%20university%20canteen%20counter%20with%20colorful%20fresh%20dishes%20in%20trays%2C%20steam%20rising%2C%20soft%20natural%20light%2C%20appetizing%20food%20photography%2C%20amber%20coral%20tones%2C%20no%20text&width=800&height=600&seq=cat-campus-food-01&orientation=landscape',
  },
  {
    id: 'heart-station',
    name: '情感小站',
    icon: 'ri-heart-3-line',
    desc: '表白墙、毕业季、校园里那些温柔的小心思',
    chip: 'bg-secondary-100 text-secondary-900',
    dot: 'bg-secondary-500',
    cover:
      'https://readdy.ai/api/search-image?query=university%20dormitory%20rooftop%20at%20dusk%20with%20warm%20string%20lights%20and%20soft%20city%20skyline%2C%20romantic%20nostalgic%20mood%2C%20coral%20and%20amber%20color%20grading%2C%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=cat-heart-station-01&orientation=landscape',
  },
  {
    id: 'career-intern',
    name: '实习求职',
    icon: 'ri-briefcase-line',
    desc: '秋招、实习、简历，学长学姐踩过的坑与经验',
    chip: 'bg-accent-100 text-accent-900',
    dot: 'bg-accent-500',
    cover:
      'https://readdy.ai/api/search-image?query=university%20career%20fair%20hall%20with%20students%20in%20conversation%20and%20booths%2C%20warm%20indoor%20lighting%2C%20amber%20and%20coral%20tones%2C%20professional%20yet%20friendly%20campus%20atmosphere%2C%20no%20text&width=800&height=600&seq=cat-career-intern-01&orientation=landscape',
  },
];

export const events: EventItem[] = [
  {
    id: 'canteen-new-window-queue',
    title: '食堂三楼新窗口上线三天，队伍从窗口排到了操场边',
    categoryId: 'campus-food',
    category: '校园美食',
    campus: '云栖大学',
    summary:
      '新开的三楼自选窗口凭一份「九块钱双拼」火速出圈，午间高峰排队时间一度超过二十分钟，同学们自发在群里更新「排队进度条」。',
    cover:
      'https://readdy.ai/api/search-image?query=warm%20university%20canteen%20serving%20window%20with%20colorful%20double%20combo%20rice%20bowls%2C%20students%20queueing%20cheerfully%2C%20soft%20daylight%2C%20appetizing%20food%20photography%2C%20amber%20coral%20tones%2C%20no%20text&width=900&height=600&seq=event-canteen-window-01&orientation=landscape',
    heat: 9821,
    comments: 341,
    publishedAt: '2026-09-08',
    readTime: '4 分钟',
    tags: ['食堂', '平价美食', '排队现场'],
    editorPick: true,
    timeline: [
      { time: '9 月 5 日 11:20', text: '三楼自选窗口低调试营业，主打九元双拼。' },
      { time: '9 月 6 日 12:05', text: '午间队伍首次绕出餐厅门口，同学开始在群里汇报排队时长。' },
      { time: '9 月 7 日 12:30', text: '队伍延伸至操场边，食堂加开一条打包快速通道。' },
      { time: '9 月 8 日 11:00', text: '食堂公告每日限定份数与出餐时段，排队秩序明显改善。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '开学第二周，云栖大学食堂三楼的自选窗口成了校园里最热的话题。窗口招牌很简单，只有一块手写板写着「九元双拼」，但真正让同学们按捺不住的，是打菜时那满满压实的一勺。',
      },
      { type: 'heading', text: '一份九块钱，为什么能排这么久' },
      {
        type: 'paragraph',
        text: '记者在现场了解到，窗口每天准备两道主荤、三道时蔬，双拼就是任选一荤配一份时蔬，米饭和汤无限续。分量足、价格稳，加上出餐速度快，让不少原本在外卖上解决午饭的同学转向了食堂。',
      },
      {
        type: 'list',
        items: [
          '定价九元，主食与例汤可续，性价比是出圈主因；',
          '现炒现打，出餐区翻台快，单人平均取餐约四十秒；',
          '窗口位于三楼，原本人流较少，这次反而成了「隐藏宝藏」。',
        ],
      },
      { type: 'heading', text: '同学们的排队智慧' },
      {
        type: 'paragraph',
        text: '排队时间长，同学们也自发形成了秩序。有人在班级群里实时更新「排队进度条」，有人错峰到下午一点后才去，还有人干脆组成「拼单小队」轮流占位。食堂方面回应，已加开一条打包快速通道，并公示每日供应份数，尽量减少等待。',
      },
      {
        type: 'quote',
        text: '「其实排队本身也蛮有意思的，大家边等边聊，比一个人点外卖热闹多了。」——现场同学',
      },
      { type: 'heading', text: '编辑观察' },
      {
        type: 'paragraph',
        text: '一间窗口能成为全校话题，背后是学生对「便宜、好吃、有烟火气」最朴素的期待。热度会退，但如果这份对味道和价格的认真能留下来，那排队就排得值。',
      },
    ],
  },
  {
    id: 'course-selection-system-crash',
    title: '选课系统开放瞬间崩溃，全校同学集体围观「抢课名场面」',
    categoryId: 'study-exam',
    category: '学业考场',
    campus: '江城理工大学',
    summary:
      '选课通道开启后系统短暂卡顿，热门通识课在数秒内被抢空，同学们在社交平台分享各类「抢课前仪式感」，也把改进建议一起提了出来。',
    cover:
      'https://readdy.ai/api/search-image?query=students%20gathered%20around%20laptops%20in%20a%20university%20library%20at%20night%20with%20warm%20desk%20lamp%20glow%2C%20focused%20expressions%2C%20candid%20campus%20documentary%20style%2C%20amber%20coral%20tones%2C%20no%20text&width=900&height=600&seq=event-course-crash-01&orientation=landscape',
    heat: 15320,
    comments: 806,
    publishedAt: '2026-09-04',
    readTime: '5 分钟',
    tags: ['选课', '通识课', '校园系统'],
    featured: true,
    timeline: [
      { time: '9 月 3 日 20:00', text: '选课通道按计划开启，热门通识课瞬间涌入大量请求。' },
      { time: '9 月 3 日 20:04', text: '部分同学反馈页面加载缓慢，系统进入限流状态。' },
      { time: '9 月 3 日 20:20', text: '教务发布说明，分时段释放余量，并延长选课窗口。' },
      { time: '9 月 4 日 09:00', text: '新增「预选志愿」功能公示，计划下轮试运行。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '每年选课周，都是校园里最紧张的时刻。今年江城理工大学选课通道开启后不久，系统出现短暂卡顿，热门通识课在几秒钟内就被选满，一时间「抢课名场面」成了校园里讨论度最高的话题。',
      },
      { type: 'heading', text: '到底发生了什么' },
      {
        type: 'paragraph',
        text: '据现场同学描述，选课页面在开抢后加载变慢，刷新几次才显示结果。由于同一时间请求集中，系统启动了限流保护。教务部门随后发布说明，承认瞬时并发超出预期，并临时延长了选课时间窗口。',
      },
      {
        type: 'list',
        items: [
          '热门通识课名额有限，供需失衡是核心原因；',
          '瞬时并发请求过高，触发系统限流保护；',
          '学校已延长选课窗口并分时段释放余量。',
        ],
      },
      { type: 'heading', text: '同学们的「抢课仪式感」' },
      {
        type: 'paragraph',
        text: '虽然过程有点曲折，同学们倒是把它玩出了花。有人提前一天整理好课程清单和备选顺序，有人在宿舍组织「多设备协作」，还有人总结出一份「抢课时间轴」分享给新生。评论区里最多的建议，是希望学校能开放预选志愿，减少「手速定胜负」的焦虑。',
      },
      {
        type: 'quote',
        text: '「抢不到不可怕，可怕的是不知道自己为什么没抢到。」——某位大一同学',
      },
      { type: 'heading', text: '后续进展' },
      {
        type: 'paragraph',
        text: '学校表示，正在评估「预选志愿 + 随机分配 + 余量补选」的组合方案，下一轮选课将尝试小范围试运行。对于同学们来说，这或许比多一次抢课机会更让人期待。',
      },
    ],
  },
  {
    id: 'dorm-orange-cat-feeding',
    title: '宿舍楼下橘猫被全校投喂，一周时间胖出了「幸福肚」',
    categoryId: 'dorm-daily',
    category: '宿舍日常',
    campus: '临海师范大学',
    summary:
      '一只常驻宿舍楼下的橘猫成了全校共同照顾的对象，同学们自发排班投喂并记录体重变化，还给它做了一块「今日营业中」的小木牌。',
    cover:
      'https://readdy.ai/api/search-image?query=cute%20orange%20tabby%20cat%20sitting%20calmly%20outside%20a%20university%20dormitory%20entrance%2C%20warm%20sunlight%2C%20cozy%20campus%20scene%2C%20soft%20amber%20and%20coral%20tones%2C%20editorial%20photography%2C%20no%20text&width=900&height=600&seq=event-dorm-cat-01&orientation=landscape',
    heat: 12045,
    comments: 512,
    publishedAt: '2026-09-06',
    readTime: '3 分钟',
    tags: ['宿舍', '校园猫', '暖新闻'],
    editorPick: true,
    timeline: [
      { time: '8 月底', text: '橘猫出现在三号宿舍楼下，性格亲人，很快被同学们认领照顾。' },
      { time: '9 月 2 日', text: '同学自发制作投喂排班表，避免重复投喂。' },
      { time: '9 月 6 日', text: '记录显示一周体重小幅上升，同学们笑称是「幸福肚」。' },
      { time: '9 月 9 日', text: '校方与同学合作，为它安排了定期体检与驱虫。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '在临海师范大学，最近人气最高的「网红」不是任何一位同学，而是一只住在三号宿舍楼下的橘猫。它每天准时出现在台阶上，见人就翻肚皮，被同学们亲切地称为「三号楼楼长」。',
      },
      { type: 'heading', text: '从一只猫到一份排班表' },
      {
        type: 'paragraph',
        text: '因为担心投喂过量，几位同学自发做了件事：把投喂点固定下来，并制作了一份简易排班表，每天由不同楼层的同学负责。表格上除了日期和名字，还贴心标注了「今日猫粮已满，请勿重复」。',
      },
      {
        type: 'list',
        items: [
          '固定投喂点，避免猫粮堆积和浪费；',
          '排班表轮值，兼顾同学们的时间与课程；',
          '记录体重与状态，发现异常及时联系校方。',
        ],
      },
      { type: 'heading', text: '一块小木牌的温度' },
      {
        type: 'paragraph',
        text: '楼下的共享区还多了一块手写小木牌，上面写着「今日营业中」，旁边放着给路过同学的猫条。有同学说，以前下楼就是赶路，现在会特意绕到台阶看一看，这也成了很多人一天里最放松的几分钟。',
      },
      {
        type: 'quote',
        text: '「照顾它的过程，其实也是在被它照顾。」——参与排班的同学',
      },
      {
        type: 'paragraph',
        text: '据校方介绍，目前已为这只橘猫安排了定期体检与驱虫，并计划在校园内推广更规范的流浪动物关爱方式，让这份温柔可以持续得更久。',
      },
    ],
  },
  {
    id: 'club-fair-drone-starry-sky',
    title: '社团招新现场神仙打架，无人机社团直接把操场变成了星空',
    categoryId: 'club-activity',
    category: '社团活动',
    campus: '青峦大学',
    summary:
      '招新季当晚，无人机社团在操场放飞了编队灯光秀，吸引了大量同学驻足围观，也让其他社团纷纷拿出看家本领，现场气氛热闹到晚上十点。',
    cover:
      'https://readdy.ai/api/search-image?query=drone%20light%20show%20above%20a%20university%20sports%20field%20at%20night%2C%20crowd%20of%20students%20watching%20in%20awe%2C%20warm%20golden%20light%20trails%2C%20coral%20and%20amber%20tones%2C%20festival%20atmosphere%2C%20no%20text&width=900&height=600&seq=event-club-drone-01&orientation=landscape',
    heat: 8760,
    comments: 289,
    publishedAt: '2026-09-02',
    readTime: '4 分钟',
    tags: ['社团', '招新', '无人机'],
    featured: true,
    timeline: [
      { time: '9 月 1 日 18:30', text: '社团招新市集开场，各社团摊位依次亮灯。' },
      { time: '9 月 1 日 19:40', text: '无人机社团进行编队灯光秀，操场瞬间安静下来。' },
      { time: '9 月 1 日 20:20', text: '灯光秀视频在校园平台刷屏，招新摊位前排起长队。' },
      { time: '9 月 2 日 10:00', text: '多个社团宣布扩招名额，报名通道延长开放。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '一年一度的社团招新，今年在青峦大学卷出了新高度。当无人机社团把三十架小飞机升上操场上空、排成校徽与星轨的图案时，原本喧闹的市集瞬间安静了几秒，随后是一片惊呼。',
      },
      { type: 'heading', text: '一场「神仙打架」的招新' },
      {
        type: 'paragraph',
        text: '无人机灯光秀之后，其他社团也纷纷拿出了看家本领：街舞社现场battle，音乐社来了段即兴合唱，天文社支起望远镜摆出「看星星」的招牌，连美食社都现场支锅煮起了糖水。招新摊位前几乎每个都排着队。',
      },
      {
        type: 'list',
        items: [
          '无人机社团以编队灯光秀开场，成为当晚流量中心；',
          '街舞、音乐、天文等社团轮番展示，形成接力式的现场氛围；',
          '多数社团在招新群里开通线上报名，减少现场排队压力。',
        ],
      },
      { type: 'heading', text: '热闹之外，同学们在选什么' },
      {
        type: 'paragraph',
        text: '围观之余，不少同学表示，真正打动他们的不是表演有多炫，而是社团里那种「一起做成一件事」的氛围。有新生说，自己在摊位前待了半小时，只为了听学长讲一次完整的项目经历。',
      },
      {
        type: 'quote',
        text: '「表演会结束，但一起熬夜改方案的日子会记很久。」——社团负责同学',
      },
      {
        type: 'paragraph',
        text: '据了解，多个社团在招新后宣布扩招名额，报名通道也延长开放。校方表示，会继续为社团活动提供场地与安全支持，让热闹留在操场上，也把成长留在同学之间。',
      },
    ],
  },
  {
    id: 'library-seat-scan-rule',
    title: '图书馆占座出新规：扫码落座、超时自动释放，同学看法两极',
    categoryId: 'campus-life',
    category: '校园生活',
    campus: '白鹭大学',
    summary:
      '图书馆上线扫码落座系统，离座超过四十分钟将自动释放座位。支持的同学认为效率提升明显，也有同学担心短时离开被误判。',
    cover:
      'https://readdy.ai/api/search-image?query=modern%20university%20library%20reading%20room%20with%20rows%20of%20seats%20and%20warm%20wooden%20furniture%2C%20quiet%20study%20atmosphere%2C%20soft%20daylight%2C%20amber%20and%20coral%20tones%2C%20editorial%20photography%2C%20no%20text&width=900&height=600&seq=event-library-seat-01&orientation=landscape',
    heat: 6540,
    comments: 433,
    publishedAt: '2026-08-30',
    readTime: '4 分钟',
    tags: ['图书馆', '占座', '新规'],
    timeline: [
      { time: '8 月 26 日', text: '图书馆在三楼与四楼试点扫码落座系统。' },
      { time: '8 月 28 日', text: '有同学反馈短暂离座被释放座位，引起讨论。' },
      { time: '8 月 29 日', text: '图书馆将释放时间由三十分钟调整为四十分钟，并开放「临时保留」。' },
      { time: '8 月 30 日', text: '系统使用率趋于稳定，空座位可见度明显提升。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '「一张桌子四个人，只有一个人在学习」——这句吐槽在很多高校图书馆里流传已久。白鹭大学图书馆最近上线了扫码落座系统，试图用一条规则解决这个老问题，也因此在校园里引发了不小的讨论。',
      },
      { type: 'heading', text: '新规是怎么运作的' },
      {
        type: 'paragraph',
        text: '同学进入自习区后，用校园卡扫码落座，系统开始计时。中途离开可以选择「临时保留」，超过规定时间未返回，座位将自动释放并重新开放预约。这一机制让长期「占而不坐」的情况大幅减少。',
      },
      {
        type: 'list',
        items: [
          '扫码落座，座位状态对所有人实时可见；',
          '临时离开可保留，超时则自动释放；',
          '释放时间根据反馈由三十分钟调整为四十分钟。',
        ],
      },
      { type: 'heading', text: '两极的看法' },
      {
        type: 'paragraph',
        text: '支持的同学认为，新规让「找座难」有了明显改善，尤其是饭点和选修课之间的碎片时间，也能找到位置。持保留意见的同学则担心，跑一趟打印店、接一个电话就可能超时，希望保留机制能更灵活。',
      },
      {
        type: 'quote',
        text: '「规则不是为了让谁难堪，而是让每个来学习的人都有位子。」——图书馆工作人员',
      },
      { type: 'heading', text: '后续' },
      {
        type: 'paragraph',
        text: '图书馆表示，会根据使用数据继续调整保留时长，并在高峰时段增设引导志愿者。一条规则能不能长久，最终还是要看它是否真正方便了大多数人。',
      },
    ],
  },
  {
    id: 'graduation-rooftop-lights',
    title: '毕业季天台告白火了，整栋宿舍楼亮灯回应「我们也祝你顺利」',
    categoryId: 'heart-station',
    category: '情感小站',
    campus: '江城理工大学',
    summary:
      '毕业前夜，一位同学在天台向四年同窗告别，对面宿舍楼依次亮灯回应，这段视频成为校园里流传最广的毕业画面。',
    cover:
      'https://readdy.ai/api/search-image?query=university%20dormitory%20rooftop%20at%20dusk%20with%20warm%20string%20lights%20and%20city%20skyline%2C%20dorm%20windows%20glowing%20warmly%2C%20nostalgic%20tender%20mood%2C%20coral%20and%20amber%20tones%2C%20no%20text&width=900&height=600&seq=event-graduation-rooftop-01&orientation=landscape',
    heat: 18930,
    comments: 1204,
    publishedAt: '2026-06-28',
    readTime: '5 分钟',
    tags: ['毕业季', '天台', '告别'],
    featured: true,
    editorPick: true,
    timeline: [
      { time: '6 月 26 日 21:00', text: '毕业生在天台用扩音器向四年同窗告别。' },
      { time: '6 月 26 日 21:05', text: '对面宿舍楼依次亮灯，同学们打开手机手电回应。' },
      { time: '6 月 26 日 21:12', text: '现场视频被同学拍下并上传校园平台。' },
      { time: '6 月 27 日 12:00', text: '视频在多个平台传播，评论区成为大型「毕业回忆现场」。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '毕业前夜，江城理工大学的宿舍区比平时安静。直到九点，天台上的灯光亮起，一位毕业生拿着扩音器，说出了那句准备了很久的话：「谢谢你们，陪我走完这四年。」',
      },
      { type: 'heading', text: '整栋楼亮起的回应' },
      {
        type: 'paragraph',
        text: '话音落下没多久，对面的宿舍楼开始有窗户亮起。先是一间，然后是整层，最后几乎整栋楼都亮了起来。有同学打开手机手电筒，有人贴着玻璃写字，还有人喊出「我们也祝你顺利」。这一幕被路过的同学拍了下来，很快传遍校园。',
      },
      {
        type: 'list',
        items: [
          '没有提前排练，亮灯完全是同学们的自发回应；',
          '现场秩序良好，宿管老师选择静静站在一旁；',
          '视频发布后，评论区变成一届又一届毕业生的回忆现场。',
        ],
      },
      { type: 'heading', text: '为什么这么多人被打动' },
      {
        type: 'paragraph',
        text: '毕业的告别有很多种，但最打动人的往往是那些没有台词的瞬间。一盏灯、一句话，就足以把四年里没说出口的话都说完。有网友留言：「看的不是别人的毕业，是自己回不去的夏天。」',
      },
      {
        type: 'quote',
        text: '「我们没有说再见，只是说，以后有机会再一起吃饭。」——视频里的毕业生',
      },
      {
        type: 'paragraph',
        text: '校方表示，同学们的行为没有影响正常秩序，学校也在毕业季开放了天台等公共空间，供大家以更安全的方式纪念这段时光。',
      },
    ],
  },
  {
    id: 'finals-week-all-nighter',
    title: '期末周自习室通宵实录：凌晨两点的走廊，全是背书声',
    categoryId: 'study-exam',
    category: '学业考场',
    campus: '临海师范大学',
    summary:
      '期末周，学校的通宵自习室连续多日满座。同学们记录下凌晨走廊的背书声、共享的提神小食和互相划重点的瞬间。',
    cover:
      'https://readdy.ai/api/search-image?query=students%20studying%20late%20at%20night%20in%20a%20university%20study%20room%2C%20warm%20lamp%20light%2C%20stacks%20of%20books%20and%20notes%2C%20cozy%20academic%20atmosphere%2C%20amber%20coral%20tones%2C%20editorial%20photography%2C%20no%20text&width=900&height=600&seq=event-finals-allnight-01&orientation=landscape',
    heat: 7120,
    comments: 268,
    publishedAt: '2026-07-05',
    readTime: '3 分钟',
    tags: ['期末周', '自习室', '通宵'],
    timeline: [
      { time: '7 月 1 日', text: '通宵自习室开放，当天晚间即接近满座。' },
      { time: '7 月 3 日 02:00', text: '同学拍下走廊背书声，视频在校园群流传。' },
      { time: '7 月 4 日', text: '自习室增设静音区与讨论区，缓解互相影响。' },
      { time: '7 月 5 日', text: '学校在自习室提供免费热水与应急文具。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '期末周的自习室，是校园里最有「战斗力」的地方。临海师范大学的通宵自习室连续几天满座，有同学把凌晨两点的走廊拍了下来：灯亮着，人靠着墙，小声背着自己的重点。',
      },
      { type: 'heading', text: '凌晨的自习室，比想象中热闹' },
      {
        type: 'paragraph',
        text: '为了照顾不同学习习惯的同学，自习室后来划分了静音区与讨论区。静音区只听见翻书声，讨论区里几颗脑袋凑在一张桌子前，把最后一章的重点一条条对过去。',
      },
      {
        type: 'list',
        items: [
          '静音区与讨论区分开，减少互相干扰；',
          '同学们自发共享提神小食与应急文具；',
          '学校提供免费热水，并提醒注意作息与安全。',
        ],
      },
      { type: 'heading', text: '一起熬夜，也没那么难熬' },
      {
        type: 'paragraph',
        text: '有人形容，期末周最治愈的不是咖啡，而是抬头时发现旁边的人也在坚持。互相划重点、互相提醒休息，让通宵这件事多了点并肩作战的意味。学校也通过广播建议大家合理安排作息，量力而行。',
      },
      {
        type: 'quote',
        text: '「第二天考完走出来，阳光很好，突然觉得熬夜也值了。」——某位大二同学',
      },
    ],
  },
  {
    id: 'military-training-formation-dance',
    title: '新生军训才艺表演出圈，教官带着方阵把操场跳成了广场',
    categoryId: 'campus-life',
    category: '校园生活',
    campus: '云栖大学',
    summary:
      '军训汇演的才艺环节，教官临时加入一段节奏欢快的集体舞，全场新生跟着一起跳，视频当晚在校园平台被大量转发。',
    cover:
      'https://readdy.ai/api/search-image?query=university%20freshmen%20military%20training%20formation%20on%20a%20sports%20field%20at%20warm%20sunset%2C%20students%20dancing%20lively%2C%20energetic%20cheerful%20campus%20scene%2C%20coral%20amber%20tones%2C%20no%20text&width=900&height=600&seq=event-military-dance-01&orientation=landscape',
    heat: 10480,
    comments: 597,
    publishedAt: '2026-09-01',
    readTime: '3 分钟',
    tags: ['军训', '新生', '汇演'],
    timeline: [
      { time: '8 月 30 日', text: '军训进入尾声，各连队准备汇演节目。' },
      { time: '8 月 31 日 17:30', text: '才艺环节教官临时加入集体舞，全场跟着跳起来。' },
      { time: '8 月 31 日 21:00', text: '现场视频在校园平台被大量转发。' },
      { time: '9 月 1 日', text: '学校官号转发并配文「这就是青春的样子」。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '军训汇演通常会以整齐的方阵收尾，但今年云栖大学的操场多了一段让人意外的插曲。才艺环节，教官临时加入了一段节奏欢快的集体舞，原本有些拘谨的新生们很快跟着跳了起来。',
      },
      { type: 'heading', text: '严肃之外的另一面' },
      {
        type: 'paragraph',
        text: '视频里，教官站在方阵最前面，动作干脆，身后的同学虽然节拍不太齐，但都笑得很开心。现场围观的高年级同学也跟着打起了拍子，操场上一时热闹非凡。',
      },
      {
        type: 'list',
        items: [
          '才艺环节由新生与教官共同完成，氛围轻松；',
          '现场秩序良好，未影响后续汇演流程；',
          '视频被学校官号转发，成为今年军训的代表画面。',
        ],
      },
      { type: 'heading', text: '一场军训，两种收获' },
      {
        type: 'paragraph',
        text: '有新生说，军训最难忘的不是晒黑了几度，而是那种「一起被罚、一起被夸」的同连队感情。教官也表示，希望在严格要求之外，让大家记住的是一段有笑声的时光。',
      },
      {
        type: 'quote',
        text: '「教官说，动作可以不标准，但笑容要标准。」——一位参演新生',
      },
    ],
  },
  {
    id: 'campus-flea-market-boom',
    title: '校园二手市集爆火：学长学姐把「传家宝」搬上了摊位',
    categoryId: 'campus-life',
    category: '校园生活',
    campus: '青峦大学',
    summary:
      '毕业季的二手市集今年格外热闹，从考研资料到小台灯、从吉他到绿植，摊主们还现场写下「使用说明书」，吸引不少新生前来寻宝。',
    cover:
      'https://readdy.ai/api/search-image?query=university%20campus%20flea%20market%20with%20second%20hand%20goods%20stalls%20on%20a%20green%20lawn%2C%20warm%20afternoon%20sunlight%2C%20lively%20student%20vendors%2C%20amber%20coral%20tones%2C%20editorial%20photography%2C%20no%20text&width=900&height=600&seq=event-flea-market-01&orientation=landscape',
    heat: 5380,
    comments: 176,
    publishedAt: '2026-06-20',
    readTime: '3 分钟',
    tags: ['毕业季', '二手市集', '好物'],
    timeline: [
      { time: '6 月 18 日', text: '二手市集在中心草坪开市，摊位数量超过去年。' },
      { time: '6 月 19 日', text: '「使用说明书」的摊位走红，摊主现场讲解物品故事。' },
      { time: '6 月 20 日', text: '市集增设以物换物区，闲置流转效率提升。' },
      { time: '6 月 21 日', text: '活动顺利结束，主办方公布物品再利用数据。' },
    ],
    content: [
      {
        type: 'paragraph',
        text: '毕业季一到，校园中心的草坪就会变成一片小小的「市集」。今年青峦大学的二手市集摊位数量明显增加，从考研资料、台灯、收纳盒，到吉他、绿植、露营椅，几乎涵盖了大学四年的全部生活。',
      },
      { type: 'heading', text: '最抢手的不是价格，是故事' },
      {
        type: 'paragraph',
        text: '不少摊主会在一张卡片上写下物品的「使用说明」：台灯陪我过了两个期末周，吉他弹会了第一首完整的歌。这些细节让原本普通的闲置物品多了几分温度，也让不少新生愿意停下来聊一聊。',
      },
      {
        type: 'list',
        items: [
          '摊主手写「使用说明书」，标注物品经历与建议；',
          '市集增设以物换物区，让闲置更快流转；',
          '主办方对未售出物品统一回收，减少浪费。',
        ],
      },
      { type: 'heading', text: '一次流转，两届人的交接' },
      {
        type: 'paragraph',
        text: '有摊主说，卖东西不是重点，重点是把四年整理一遍，把还能用的东西交到需要的人手里。也有新生表示，从学长学姐手里接过的不只是一盏灯，还有一种「好好生活」的态度。',
      },
      {
        type: 'quote',
        text: '「东西旧了点，但故事还是新的。」——市集摊主',
      },
    ],
  },
];